import { gql } from '@apollo/client';
import defer from 'lodash/defer';

import { useOnValidate } from 'modules/queryBuilder/hooks/useOnValidate';
import { CompleteQuery, EditableQuery } from 'modules/queryBuilder/types';
import { useQueryErrorStateContext } from 'modules/queryBuilder/contexts';
import { aggregatePathError } from 'modules/queryBuilder/utils/aggregatePathError';
import { useLazyGraphql } from 'utils/hooks/useLazyGraphql';
import { isNonEmptyArray } from 'utils/tsHelpers';

import { useSegmentationQueryContext } from '../contexts/segmentationQuery';
import { useSegmentationQuerySchema } from '../components/queryBuilderSection/schema/hooks';
import { useTranslation } from '../hooks';

const descriptionMaxLength = 255;

export const FetchSuggestedQueryDescriptionQuery = gql`
  query getSuggestQueryDescription($query: String!, $listId: Long) {
    suggestQueryDescription(query: $query, listId: $listId) {
      ... on FailedToSuggestDescriptionError {
        message
      }
      ... on SuggestedDescription {
        content
      }
    }
  }
`;

export enum FetchDescriptionType {
  Editable = 'editable',
  Complete = 'complete',
}

export type FetchDescriptionPayload = {
  onValidQuery?: () => void;
  onInvalidQuery?: () => void;
};

export type UseSuggestedDescriptionProps = {
  listId?: number;
  query: CompleteQuery | EditableQuery;
};

export type UseSuggestedDescriptionReturn = {
  description: string; // Full description returned by AI
  descriptionInput: string; // description trimmed to max length of input (db field lenght max)
  error: string;
  fetchDescription: (
    { onValidQuery, onInvalidQuery }: FetchDescriptionPayload,
    fetchType?: FetchDescriptionType,
  ) => void;
  isLoading: boolean;
};

export const useSuggestedDescription = ({
  query,
  listId,
}: UseSuggestedDescriptionProps): UseSuggestedDescriptionReturn => {
  const { t } = useTranslation();
  const { fetch, isLoading, errorMessage, data } = useLazyGraphql(
    FetchSuggestedQueryDescriptionQuery,
    {
      notifyOnNetworkStatusChange: true,
    },
  );
  const { setIsValidQuery } = useSegmentationQueryContext();
  const { setPathErrors, pathWarnings } = useQueryErrorStateContext();
  const segmentationQuerySchema = useSegmentationQuerySchema();

  const getParams = (completeQuery: CompleteQuery) => ({
    query: JSON.stringify(completeQuery),
    ...(listId && { listId }),
  });

  const onValidate = useOnValidate({
    schema: segmentationQuerySchema,
    onSuccessCb: ({ onInvalidQuery, onValidQuery, parsedQuery }) => {
      // success callback is fired specifically for the lack of errors, not warnings
      if (isNonEmptyArray(pathWarnings)) {
        // warnings only
        if (onInvalidQuery) {
          defer(onInvalidQuery); // defer to allow input ref to be valid
        }
      } else {
        // no error or warnings
        onValidQuery?.();
        fetch(getParams(parsedQuery));
      }
      setPathErrors([]);
      setIsValidQuery(true);
    },
    onErrorCb: ({ errors, onInvalidQuery }) => {
      const pathErrorsFromSchema = errors.flatMap(aggregatePathError);
      if (onInvalidQuery) {
        defer(onInvalidQuery); // defer to allow input ref to be valid
      }
      setPathErrors(pathErrorsFromSchema);
      setIsValidQuery(false);
    },
    editableQuery: query as EditableQuery,
  });

  // fetch description for editable query (validates first)
  const fetchDescription = (
    { onValidQuery, onInvalidQuery }: FetchDescriptionPayload,
    fetchType: FetchDescriptionType = FetchDescriptionType.Editable,
  ) => {
    if (fetchType === FetchDescriptionType.Editable) {
      // when fetching with an EditableQuery, validate first (passing in validation callbacks)
      onValidate({ onValidQuery, onInvalidQuery });
    } else {
      // when fetching with a CompleteQuery, skip the validation
      fetch(getParams(query as CompleteQuery));
    }
  };

  const isGraphQLError = !!errorMessage?.message && errorMessage?.name === 'ApolloError';

  const getErrorMessage = (err: string, isGQL: boolean) => {
    if (isGQL) {
      switch (err) {
        case 'Failed to fetch':
          return t('errors.failedToFetch');
        default:
          return err;
      }
    }
    return err;
  };

  const description = data?.suggestQueryDescription?.content || '';

  return {
    description,
    descriptionInput: description.trim().substring(0, descriptionMaxLength),
    error: getErrorMessage(
      errorMessage?.message || data?.suggestQueryDescription?.message || '',
      isGraphQLError,
    ),
    fetchDescription,
    isLoading,
  };
};
